import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    FormControlLabel,
    Switch,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Save,
    Cancel
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { collection, addDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { Consultant } from './ConsultantList';

const ConsultantForm: React.FC = () => {
    const { user, userProfile, isTenantAdmin, isGlobalAdmin } = useUser();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);

    useDocumentTitle(isEditing ? 'Edit Consultant' : 'Add Consultant');

    const [formData, setFormData] = useState<Partial<Consultant>>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        jobTitle: '',
        country: '',
        isRegisteredUser: false,
        isActive: true
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isEditing && id) {
            loadConsultant();
        }
    }, [isEditing, id]);

    const loadConsultant = async () => {
        try {
            setLoading(true);
            setError('');

            let consultantData: Partial<Consultant> | null = null;

            // Fallback: Load directly from Firestore
            if (!consultantData && id) {
                const consultantRef = doc(db, 'consultants', id);
                const consultantDoc = await getDoc(consultantRef);

                if (consultantDoc.exists()) {
                    consultantData = { id: consultantDoc.id, ...consultantDoc.data() } as Partial<Consultant>;
                } else {
                    setError('Consultant not found');
                    return;
                }
            }

            if (consultantData) {
                setFormData(consultantData);
            }
        } catch (error: any) {
            console.error('Error loading consultant:', error);
            setError(error.message || 'Failed to load consultant');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof Consultant, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        if (!user) return;
        try {
            setSaving(true);
            setError('');

            // Validate required fields
            if (!formData.firstName?.trim()) {
                setError('First name is required');
                return;
            }
            if (!formData.lastName?.trim()) {
                setError('Last name is required');
                return;
            }
            if (!formData.email?.trim()) {
                setError('Email is required');
                return;
            }
            if (!formData.company?.trim()) {
                setError('Company is required');
                return;
            }
            if (!formData.country?.trim()) {
                setError('Country is required');
                return;
            }

            // Email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (formData.email && !emailRegex.test(formData.email)) {
                setError('Please enter a valid email address');
                return;
            }

            // Prepare consultant data
            const consultantData = {
                ...formData,
                tenantId: userProfile?.tenantId,
                createdBy: user.uid,
                updatedAt: new Date().toISOString()
            };

            // Fallback: Save directly to Firestore
            try {
                if (isEditing && id) {
                    // Update existing consultant
                    const consultantRef = doc(db, 'consultants', id);
                    await updateDoc(consultantRef, {
                        ...consultantData,
                        updatedAt: new Date()
                    });
                } else {
                    // Create new consultant
                    await addDoc(collection(db, 'consultants'), {
                        ...consultantData,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    });
                }

                navigate('/consultants');
            } catch (firestoreError: any) {
                setError(firestoreError.message || `Failed to ${isEditing ? 'update' : 'create'} consultant`);
            }
        } catch (error: any) {
            console.error('Error saving consultant:', error);
            setError(error.message || `Failed to ${isEditing ? 'update' : 'create'} consultant`);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate('/consultants');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        {isEditing ? 'Edit Consultant' : 'Add Consultant'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {isEditing ? 'Update consultant information' : 'Add a new consultant to the system'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" onClick={handleCancel} startIcon={<Cancel />}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} startIcon={<Save />} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : (isEditing ? 'Update' : 'Save')}
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={formData.firstName}
                                onChange={(e) => handleChange('firstName', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={formData.lastName}
                                onChange={(e) => handleChange('lastName', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Phone"
                                value={formData.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Company"
                                value={formData.company}
                                onChange={(e) => handleChange('company', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Job Title"
                                value={formData.jobTitle}
                                onChange={(e) => handleChange('jobTitle', e.target.value)}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Country"
                                value={formData.country}
                                onChange={(e) => handleChange('country', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isRegisteredUser}
                                            onChange={(e) => handleChange('isRegisteredUser', e.target.checked)}
                                        />
                                    }
                                    label="Registered User"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => handleChange('isActive', e.target.checked)}
                                        />
                                    }
                                    label="Active"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ConsultantForm;
